<template>
  <div class="owal fixed navigation menu">
    <div class="owal navigation menu header">
      <div class="owal navigation menu hamburger button" @click="menuIsOpen=!menuIsOpen"/>
      <router-link id="home-button" :to="hompagePath">
        <img :id="logoId"
          src="../../assets/img/nav/Owal O Light@2x.png"
          alt="O"
        >
        <img
          id="static-logo"
          src="../../assets/img/nav/Owal WAL Light@2x.png"
          alt="WAL"
        >
      </router-link>
      <domain-picker
        v-if="this.authenticated"
        id="domain-picker"
        class="scrolling dropdown item"
        display-property="description"
        key-property="uuid"
        :items="domains"
        @select-domain="domainChanged"
      />
    </div>
    <div :class="`owal navigation menu content ${menuIsOpen ? 'expanded' : 'collapsed'}`">
      <div class="owal navigation menu group">
        <nav-button
          v-for="route in authRoutes"
          :key="route.path"
          class="owal navigation menu link"
          :route="route"
          @click.native="menuIsOpen=false"
        />
        <staff-tools
          class="owal navigation menu group staff"
          @link-activated="menuIsOpen=false"
        />
        <!-- Note: the extra login button is a quick patch
        to show the logout flow for mobile users. -->
        <login-button
          v-if="authenticated && menuIsOpen"
          class="owal navigation menu link"
          id="mobile-logout-button"
          />
      </div>
      <div class="owal navigation menu group login">
        <nav-button
          v-for="route in visibleRoutes"
          :key="route.path"
          :route="route"
          class="owal navigation menu link"
        />
        <login-button
          id="desktop-logout-button"
          v-if="authenticated"
          class="owal navigation menu link"
        />
      </div>
    </div>
  </div>
</template>

<script>
import NavButton from '@/components/shared/NavigationBarButton'
import DomainPicker from '@/components/shared/NavigationBarDomainPicker'
import StaffTools from '@/components/staff/StaffTools'
import LoginButton from '@/components/shared/LoginButton'

import { navBarRoutesFromRoutes, authenticatedRoutesFromRoutes, publicRoutesFromRoutes } from '@/libraries/nav/filters.js'
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'NavigationBar',
  data () {
    return {
      menuIsOpen: false,
    }
  },
  computed: {
    ...mapGetters({
      authenticated: 'isAuthenticated',
      title: 'title',
      domains: 'domains',
      canAccessService: 'entitlements/canAccessService',
      refreshing: 'refreshing',
      currentDomainUUID: 'currentDomainUUID',
      canPerformOperationForService: 'entitlements/canPerformOperationForService',
      preferences: 'preferences',
    }),
    logoId () {
      return `animated-logo${this.refreshing ? '_refreshing' : ''}`
    },
    hompagePath () {
      return this.authenticated ? '/dashboard' : '/'
    },
    isShowingDashboard () {
      return this.$route.path === '/dashboard'
    },
    visibleRoutes () {
      const routes = this.$router.options.routes

      const navbarRoutes = navBarRoutesFromRoutes(routes)
      const publicRoutes = publicRoutesFromRoutes(navbarRoutes)
      return publicRoutes
    },
    authRoutes () {
      const routes = this.$router.options.routes
      const isAuthenticated = this.authenticated

      const navbarRoutes = navBarRoutesFromRoutes(routes)
      const authRoutes = authenticatedRoutesFromRoutes(navbarRoutes, isAuthenticated)
      const authorizedRoutes = authRoutes.filter(route => {
        if (route.meta.domainPreference) {
          return this.preferences[route.meta.domainPreference] && this.canAccessService(route.meta.service)
        }
        if (route.meta.operation) {
          return this.canPerformOperationForService(route.meta.service, route.meta.operation)
        } else return this.canAccessService(route.meta.service)
      })
      return authorizedRoutes
    },
  },
  methods: {
    ...mapActions(['selectDomain']),
    ...mapMutations(['makeDomainCurrentByCameraUUID']),
    domainChanged (domain) {
      // Using mapGetters seems to fail with getters that take parameters, since they return functions?
      const currentCameraUUID = this.$store.getters.defaultCameraUUIDForDomainUUID(domain.uuid)
      this.updateURLWithCameraUUID(currentCameraUUID)
      this.selectDomain(domain)
    },
    updateURLWithCameraUUID (cameraUUID, completion) {
      if (this.$route.path.startsWith('/view')) {
        const timestamp = this.$store.state.route.params.timestamp || ':timestamp'
        const url = `/view/${cameraUUID}/${timestamp}`
        const router = this.$router
        router.push(url, completion)
      }
    },
  },
  components: {
    NavButton,
    DomainPicker,
    StaffTools,
    LoginButton,
  },
}
</script>

<style scoped>
/* Fixed Navigation Menu - All Screens */
.owal.fixed.navigation.menu {
  position: fixed;
  top: 0;
  height: 44px;
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: 0;
  padding: 0;
  z-index: 900;
  border-radius: 0 !important;
  background: #324057;
  border: none; /*1px solid rgba(34,36,38,.15);*/
  box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15);
  font-size: 1rem;
  font-weight: 400;
}

.owal.navigation.menu.hamburger.button {
  height: 45px;
  width: 45px;
  background-image: url('../../assets/img/Buttons/Hamburger@3x.png');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
}

#domain-picker {
  color: #fff;
}

/* Desktop Styles */
@media only screen and (min-width: 961px) {
  #mobile-logout-button {
    display: none;
  }

  /* Root Element */
  .owal.fixed.navigation.menu {
    flex-direction: row;
    align-content: center;
    align-items: center;
  }

  /* Hide the hamburger button on desktop. */
  .owal.menu.hamburger.button {
    display: none;
  }

  /* Logo + Domain Dropdown*/
  .owal.navigation.menu.header {
    display: flex;
    flex-direction: row;
    align-content: bottom;
    align-items: center;
    margin-left: 3.81rem;
  }

  /* Non-header area. Always visible on large screens. */
  .owal.navigation.menu.content.collapsed,
  .owal.navigation.menu.content.expanded {
    display: flex;
    flex-grow: 2;
    justify-content: space-between;
  }

  /* Menu Group */
  .owal.owal.navigation.menu.group {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 44px;
  }

  .owal.navigation.menu.group.staff,
  .owal.navigation.menu.group.login {
    align-self: flex-end;
  }

  /* Links */
  .owal.navigation.menu.link {
    font-family: 'HelveticaNeue' 'Helvetica', sans;
    font-size: 16px;
    color: #ffffff;
    letter-spacing: 0;
    margin-left: 0.8rem;
    margin-right: 0.8rem;
    height: 44px;
    display: flex;
    align-items: center;
  }

  .owal.navigation.menu.content.expanded {
    display: flex;
    max-height: 2.85714286em;
  }
}

/* Tablet Styles */
@media only screen and (min-width: 768px) and (max-width: 960px) {
  #mobile-logout-button {
    display: none;
  }

  /* Menu Button */
  .owal.navigation.menu.hamburger.button {
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    height: 2.81rem;
    width: 2.13rem;
    margin: 0.81rem 1.13rem;
  }

  /* Root Element */
  .owal.fixed.navigation.menu {
    flex-direction: column;
    justify-content: center;
    align-content: center;
  }

  /* Logo + Domain Dropdown*/
  .owal.navigation.menu.header {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  /* Collapsed Navigation Menu */
  .owal.navigation.menu.content.collapsed,
  .owal.navigation.menu.content.collapsed * {
    display: none;
    opacity: 0;
  }

  /* Expanded Navigation Menu */
  .owal.navigation.menu.content.expanded {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    opacity: 1;
  }

  /* Expanded Navigation Menu Link */
  .owal.navigation.menu.content .owal.navigation.menu.link {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: #c0ccda;
    width: 100%;
    height: 44px;
    padding-left: 1rem;
    font-family: Avenir-Black;
    font-size: 1.25rem;
    color: #475669;
  }

  .owal.navigation.menu.group.staff {
    order: 10;
    align-self: auto;
  }

  .owal.navigation.menu.group.login {
    order: 9;
    align-self: auto;
  }
}

/* Mobile, wide enough for landscape */
@media only screen and (max-width: 768px) {
  #desktop-logout-button {
    display: none;
  }

  /* Root Element */
  .owal.fixed.navigation.menu {
    position: fixed;
    top: 0;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    flex-shrink: 0;
  }

  /* Logo + Domain Dropdown*/
  .owal.navigation.menu.header {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
  }

  /* Menu Button */
  .owal.navigation.menu.hamburger.button {
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    height: 2.81rem;
    width: 2.13rem;
    margin: 0.5rem 0.5rem;
  }

  /* Animation Support */
  .owal.navigation.menu.link {
    transition: opacity 400ms ease;
    opacity: inherit;
  }

  /* Menu Dropdown */
  .owal.navigation.menu.content {
    position: fixed;
    top: 3.1459rem;
    bottom: 0;
    left: 0;
    right: 0;
    -webkit-transform-origin: 50% 0%;
    transform-origin: 50% 0%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Collapsed Navigation Menu */
  .owal.navigation.menu.content.collapsed {
    height: 0;
    overflow: hidden;
    animation: menu-out 300ms ease normal;
    -webkit-transform: perspective(350px) rotateX(-90deg);
    transform: perspective(350px) rotateX(-90deg);
  }

  /* Expanded Navigation Menu */
  .owal.navigation.menu.content.expanded {
    height: 100%;
    transition: all 300ms ease;
    background: rgba(50, 50, 50, 0.5);
    -webkit-backdrop-filter: blur(2px);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin: 0;
    transition: all 300ms ease;
    animation: menu-in 300ms ease normal;
  }

  /* Expanded Navigation Menu Link */
  .owal.navigation.menu.link {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: #c0ccda;
    width: 100%;
    height: 44px;
    padding-left: 1rem;
    font-family: Avenir-Black;
    font-size: 20px;
    color: #475669;
    opacity: inherit;
  }

  .owal.navigation.menu.group.staff {
    order: 10;
    align-self: auto;
  }

  .owal.navigation.menu.group.login {
    order: 9;
    align-self: auto;
  }
}

#home-button {
  display: flex;
  flex-direction: row;
  padding: 0;
  margin: 0;
  height: 20px;
  width: 80px;
}

/*
      The O in the Owal logo is exported to 40px squared.
      The WAL is 40px tall and 120px wide.

      The numbers used here are scaled proportionally.
    */

#home-button #static-logo {
  height: 20px;
  width: 60px;
  margin: 0;
  padding: 0;
}

#home-button #animated-logo,
#home-button #animated-logo_refreshing {
  height: 20px;
  width: 20px;
  margin: 0;
  padding: 0;
}

#home-button #animated-logo {
  animation: logo-spin-reverse 1 0.15s linear;
}

#home-button #animated-logo_refreshing {
  animation: slow-spin infinite 1s linear;
}

#home-button:hover #animated-logo {
  animation: logo-spin 1 0.15s linear;
}

@keyframes logo-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(180deg);
  }
}

@keyframes logo-spin-reverse {
  from {
    transform: rotate(180deg);
  }
  to {
    transform: rotate(0deg);
  }
}

@keyframes slow-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes menu-in {
  0% {
    opacity: 0;
    transform: perspective(350px) rotateX(-90deg);
  }
  /* 33%  {
        transform: perspective(350px) rotateX(0deg);
      }
      66% {
        transform: perspective(350px) rotateX(10deg);
      }  */
  100% {
    transform: perspective(350px) rotateX(0deg);
  }
}

@keyframes menu-out {
  0% {
    height: 100%;
    transform: perspective(350px) rotateX(0deg);
  }
  99% {
    height: 100%;
    opacity: 0;
    transform: perspective(350px) rotateX(-90deg);
  }
  100% {
    height: 0;
  }
}

@media only print {
  .owal.fixed.navigation.menu {
    display: none;
  }
}
</style>
